<script setup lang="ts">
import { computed } from 'vue'

import { cn, CONTROL_HEIGHT, CONTROL_PADDING, INPUT_BASE, INPUT_INVALID } from './classes'
import type { ControlSize } from './types'

/**
 * Textový vstup.
 *
 * Zámerne nemá vlastný label — ten patrí do `FormField`, ktorý mu podá `id`
 * aj `aria-describedby`. Vďaka tomu nemôže vzniknúť pole bez labelu.
 *
 * `type="date"` používa natívny výber dátumu prehliadača. Nie je to ozdoba:
 * dátum písaný ručne do textového poľa skončí pri preklepe ako prázdny termín
 * a nikto si to nevšimne.
 */
const props = withDefaults(
	defineProps<{
		id?: string
		type?: 'text' | 'email' | 'password' | 'search' | 'tel' | 'url' | 'date'
		size?: ControlSize
		placeholderSk?: string
		invalid?: boolean
		disabled?: boolean
		readonly?: boolean
		autocomplete?: string
		describedBy?: string
		/** Čísla a kódy sa píšu mono s tabular-nums (`.tabular` z tokens.css). */
		mono?: boolean
	}>(),
	{ type: 'text', size: 'md' },
)

const model = defineModel<string>({ default: '' })

const classes = computed(() =>
	cn(
		INPUT_BASE,
		CONTROL_HEIGHT[props.size],
		CONTROL_PADDING[props.size],
		props.invalid === true ? INPUT_INVALID : '',
		props.mono === true ? 'tabular' : '',
	),
)
</script>

<template>
	<input
		:id="props.id"
		v-model="model"
		:type="props.type"
		:class="classes"
		:placeholder="props.placeholderSk"
		:disabled="props.disabled"
		:readonly="props.readonly"
		:autocomplete="props.autocomplete"
		:aria-invalid="props.invalid === true ? 'true' : undefined"
		:aria-describedby="props.describedBy"
	>
</template>
